import React, { useMemo, useRef, useState } from 'react';

import { widgetComponents } from 'config/widgetConfigurations/widgetComponents';
import { widgetsConfigurations } from 'config/widgetConfigurations/widgetsConfigurations';
import { useParams } from 'react-router-dom';
import { emitHover } from 'services/eventBus/hoverBus';
import { WidgetView } from 'utilitys/reporting/widgetViewModeUtils';

import { useCustomLatestCompareToVersions } from 'hooks/useCustomLatestCompareToVersions';
import useUserHook from 'hooks/useUserHook';

import ErrorBoundary from 'components/common/ErrorBoundary/ErrorBoundary';
import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';

import { Button } from 'components/DesignSystem/Button';
import { Narrative } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/Narrative/narrative';
import { PinboardOrReportWidgetWrapperProvider } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/context/widgetWrapperContext';
import { useNarrative } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/hooks/useNarrative';
import { getPermissionsToViewComponent } from 'components/Dashboards/Project/Components/CustomDashboard/customDashboard.utils';
import { NoPermissionWidget } from 'components/Dashboards/Project/Components/CustomDashboard/noPermissionWidget';
import { IHashmap } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { WidgetButton } from 'components/Dashboards/Reporting/_common/widgetButtons/WidgetButton';
import { getSizeByDimensions } from 'components/Dashboards/Reporting/reporting.utils';
import { useAddAutomatedNarrativeWidgetToReport } from 'components/Dashboards/Reporting/threeDotsMenu/hooks/useAddAutomatedNarrativeWidgetToReport';
import { ButtonsSeparator } from 'components/Dashboards/Reporting/widgetInReport/components/buttonsSeparator/ButtonsSeparator';
import { SelectedProjectContractView } from 'components/Dashboards/Reporting/widgetInReport/components/selectedProjectContractView/SelectedProjectContractView';
import { WidgetButtonsWrapper } from 'components/Dashboards/Reporting/widgetInReport/components/widgetButtonsWrapper/WidgetButtonsWrapper';
import { WidgetDataDevMode } from 'components/Dashboards/Reporting/widgetInReport/components/widgetDataDevMode/WidgetDataDevMode';
import { WidgetErrorFallback } from 'components/Dashboards/Reporting/widgetInReport/components/widgetErrorFallback/WidgetErrorFallback';
import { useHighlightNarrativeRelationOnHover } from 'components/Dashboards/Reporting/widgetInReport/hooks/highlightNarrativeRelationOnHover/useHighlightNarrativeRelationOnHover';
import { useGetProjectAndContractById } from 'components/Dashboards/Reporting/widgetInReport/hooks/useGetProjectAndContractById';
import { ReportPageUrlParams } from 'components/pages/ReportPage/ReportPage';
import { NarrativeWidgetsRelationsMapType } from 'components/pages/ReportPage/hooks/useNarrativesMaps';

import { Widget, WidgetSizes } from '../reporting.types';
import classes from './WidgetInReport.module.scss';

interface Props {
    widget: Widget;
    projectHierarchyList: IProjectHierarchyList[];
    onEdit?: () => void;
    onDelete?: () => void;
    componentGridSize: { h: number; w: number };
    changeWidgetSize: (widgetId: string, widgetSize: WidgetSizes, widgetComponentKey: keyof IHashmap) => void;
    narrativeWidgetsRelationsMap: NarrativeWidgetsRelationsMapType;
    isFullscreen?: boolean;
}

export const WidgetInReport = ({
    widget,
    onEdit,
    onDelete,
    projectHierarchyList,
    changeWidgetSize,
    componentGridSize,
    narrativeWidgetsRelationsMap,
    isFullscreen = false,
}: Props) => {
    const widgetInReportContainerRef = useRef<HTMLDivElement | null>(null);
    const reportId = Number(useParams<ReportPageUrlParams>().reportId);
    const [isNarrativeEdit, setIsNarrativeEdit] = useState<boolean>(false);
    const handleClickNarrative = () => setIsNarrativeEdit(!isNarrativeEdit);
    const { ability, email } = useUserHook();

    const { project, contract } = useGetProjectAndContractById({
        projectId: widget.projectId,
        contractId: widget.contractId,
        projectHierarchyList,
    });

    const componentObj = useMemo(() => {
        return Boolean(widget.componentKey) ? widgetsConfigurations[widget.componentKey as keyof IHashmap] : null;
    }, []);
    if (!componentObj) return null;
    const { NoPermissionMessage, permission, titleWithoutProjectAndContract, reportConfig } = componentObj;
    const { Component: Widget } = widgetComponents[widget.componentKey];
    // Widget state persisted in backend (JSON in widget.params); used as the widget’s initial state.
    const widgetState: string | null = useMemo(() => {
        return widget.params ? JSON.parse(widget.params) : null;
    }, [widget.params]);

    // NOTE: In reports, widgets compare latest vs. baseline version IDs (or latest vs. previous for certain widgets)
    const { latestVersionId, compareVersionId, latestVersionTitle, compareVersionTitle } =
        useCustomLatestCompareToVersions({
            projectId: widget.projectId,
            contractId: widget.contractId,
            componentKey: widget.componentKey as keyof IHashmap,
        });

    const { isHavePermissions } = getPermissionsToViewComponent({
        projectHierarchyList,
        projectId: widget.projectId,
        permission: permission !== undefined ? ability.can('view', permission) : undefined,
    });

    const { narrativeDescription, isGenerativeAi, setIsGenerativeAi, isLoading } = useNarrative({
        email,
        widgetId: widget.id.toString(),
        showGenerativeNarrative: widget.showNarrative ?? false,
        description: widget.description,
    });

    useHighlightNarrativeRelationOnHover({
        containerRef: widgetInReportContainerRef,
        widgetId: widget.id,
        relationsMap: narrativeWidgetsRelationsMap,
    });

    const { onAddAutomatedNarrative } = useAddAutomatedNarrativeWidgetToReport({
        parentComponentKey: widget.componentKey,
    });

    const shouldRenderEditButton = reportConfig?.buttons?.edit !== false;
    // Show "Add AI narrative" button when widget supports automated narratives
    // Button is disabled when narrative already exists, enabled when removed
    const widgetSupportsAutomatedNarrative = reportConfig?.threeDotMenuOptions?.useAutomatedNarrative === true;
    const widgetAlreadyHasNarrative = narrativeWidgetsRelationsMap.has(widget.id);
    const shouldRenderAddAiNarrativeButton = widgetSupportsAutomatedNarrative;
    const shouldRenderAllButtons = reportConfig?.hideAllButtons !== true;
    const selectedWidgetSize = getSizeByDimensions(componentGridSize) ?? ('custom' satisfies WidgetSizes);
    const shouldRenderNarrative = reportConfig?.hideNarrativeEditor !== true;

    return (
        <PinboardOrReportWidgetWrapperProvider
            value={{
                isGenerativeAi,
                setIsGenerativeAi,
                componentKey: widget.componentKey as keyof IHashmap,
                isLoading,
                onEditWidget: onEdit,
                onToggleNarrative: handleClickNarrative,
                onDeleteWidget: onDelete,
                onWidgetChangeSize: changeWidgetSize,
                className: classes.scrollable,
                selectedWidgetSize,
                isFullscreen,
            }}
        >
            <div
                ref={widgetInReportContainerRef}
                className={`${classes.widgetInReport}`}
                onPointerEnter={() => emitHover(widget.id)}
                onPointerLeave={() => emitHover(null)}
            >
                <div className={classes.content}>
                    {isHavePermissions && Widget ? (
                        <ErrorBoundary fallback={<WidgetErrorFallback />}>
                            <WidgetDataDevMode widgetId={widget.id} componentKey={widget.componentKey} />
                            <Widget
                                widgetViewMode={WidgetView.reportView}
                                widgetSelectedProjectAndContract={
                                    <SelectedProjectContractView
                                        projectName={project?.name}
                                        contractName={contract?.name}
                                        latestVersionTitle={latestVersionTitle}
                                        compareVersionTitle={compareVersionTitle}
                                        componentKey={widget.componentKey}
                                    />
                                }
                                widgetId={widget.id}
                                projectId={widget.projectId}
                                contractId={widget.contractId}
                                latestVersionId={latestVersionId}
                                compareVersionId={compareVersionId}
                                localFilters={widgetState}
                                title={widget.name}
                                externalComponents={[
                                    shouldRenderAllButtons ? (
                                        <WidgetButtonsWrapper key={'buttonsWrapper'}>
                                            {shouldRenderAddAiNarrativeButton && (
                                                <Button
                                                    variant={'tertiary'}
                                                    startIcon={
                                                        <MuiIcon
                                                            icon={'auto_awesome'}
                                                            fontSize={'16px'}
                                                            color={'inherit'}
                                                        />
                                                    }
                                                    disabled={widgetAlreadyHasNarrative}
                                                    onClick={() => {
                                                        onAddAutomatedNarrative({
                                                            widgetId: widget.id,
                                                            shareTitle: widget.name,
                                                            projectId: widget.projectId,
                                                            contractId: widget.contractId,
                                                        });
                                                    }}
                                                >
                                                    Add AI narrative
                                                </Button>
                                            )}
                                            {shouldRenderEditButton && (
                                                <WidgetButton
                                                    icon={'edit'}
                                                    onClick={onEdit}
                                                    tooltipContent={'Edit component'}
                                                />
                                            )}
                                            <ButtonsSeparator />
                                        </WidgetButtonsWrapper>
                                    ) : null,
                                ]}
                                editNarrative={
                                    shouldRenderNarrative && (
                                        <Narrative
                                            isEdit={isNarrativeEdit}
                                            value={narrativeDescription || ''}
                                            handleCancel={() => setIsNarrativeEdit(false)}
                                            handleSave={() => setIsNarrativeEdit(false)}
                                            widgetId={widget.id.toString()}
                                            reportId={reportId}
                                        />
                                    )
                                }
                            />
                        </ErrorBoundary>
                    ) : (
                        <NoPermissionWidget NoPermissionMessage={NoPermissionMessage} />
                    )}
                </div>
            </div>
        </PinboardOrReportWidgetWrapperProvider>
    );
};
